import React, { useCallback, useEffect, useState, useRef } from 'react';
import { SunIcon, MoonIcon, BellIcon, UserIcon, XIcon, CheckIcon, MenuIcon, TargetIcon, HelpCircleIcon, LogOutIcon } from 'lucide-react';
import { HelpDrawer } from './HelpDrawer';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useData } from '../../contexts/DataContext';
import { getRoleName } from '../../utils/helpers';
import { usePermissions } from '../../hooks/usePermissions';
interface HeaderProps {
  isCollapsed?: boolean;
  onMobileMenuToggle?: () => void;
  onNavigate?: (module: string) => void;
  currentModule?: string;
}
const DEFAULT_GOALS_CONFIG = {
  maxMinutesToSchedule: 120,
  warningMinutesBefore: 30,
  initialArea: 'Recepción',
  escalationArea: 'Call Center',
  reminders: 1
};
export function Header({
  isCollapsed = false,
  onMobileMenuToggle,
  onNavigate,
  currentModule
}: HeaderProps) {
  const {
    user,
    logout
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const {
    patients,
    appointments
  } = useData();
  const {
    canView
  } = usePermissions();
  const [showHelp, setShowHelp] = useState(false);
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification
  } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [goalsAlertCount, setGoalsAlertCount] = useState(0);
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const calculateGoalsAlerts = useCallback(() => {
    try {
      const raw = localStorage.getItem('savidna-goals-config');
      const config = raw ? {
        ...DEFAULT_GOALS_CONFIG,
        ...JSON.parse(raw)
      } : DEFAULT_GOALS_CONFIG;
      const maxMs = config.maxMinutesToSchedule * 60000;
      const warnMs = config.warningMinutesBefore * 60000;
      const now = new Date().getTime();
      let alertCount = 0;
      patients.forEach(p => {
        const createdAt = new Date(p.createdAt).getTime();
        if (isNaN(createdAt)) return;
        const deadline = createdAt + maxMs;
        const firstAppt = appointments.filter(a => a.patientId === p.id).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0];
        const scheduledAt = firstAppt ? new Date(firstAppt.createdAt).getTime() : null;
        if (!scheduledAt) {
          if (now > deadline) {
            alertCount++; // vencida
          } else if (deadline - now <= warnMs) {
            alertCount++; // proxima a vencer
          }
        } else if (scheduledAt > deadline) {
          alertCount++; // vencida (agendada tarde)
        }
      });
      setGoalsAlertCount(alertCount);
    } catch (e) {
      console.error('Error calculating goals alerts', e);
    }
  }, [patients, appointments]);
  useEffect(() => {
    calculateGoalsAlerts();
    const interval = setInterval(calculateGoalsAlerts, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [calculateGoalsAlerts]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <>
    <header className={`h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-3 sm:px-4 md:px-6 fixed top-0 right-0 z-40 transition-all duration-300 ease-in-out
        left-0
        ${isCollapsed ? 'md:left-[68px]' : 'md:left-64'}
      `} data-id="element-151" data-mp="3cvhzz44wqhps">
      
      {/* Left side */}
      <div className="flex items-center gap-3 min-w-0" data-id="element-152" data-mp="3oczfs19ylm2p">
        {/* Mobile menu button */}
        <button onClick={onMobileMenuToggle} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors md:hidden flex-shrink-0" aria-label="Abrir menú" data-id="element-153" data-mp="3je4kw0ypw728">
          
          <MenuIcon className="w-5 h-5" data-id="element-154" data-mp="aycnpzjwito4" />
        </button>
        <div className="min-w-0" data-id="element-155" data-mp="awrt69xl7aq9">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate" data-id="element-156" data-mp="5z8bf01okftk">
            {user?.name?.split(' ')[0] || user?.name}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate" data-id="element-157" data-mp="2vpv97rv7wzot">
            {user?.role && getRoleName(user.role)}
          </p>
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0" data-id="element-158" data-mp="2gjfqrqttopp4">
        {/* Goals Alert */}
        {onNavigate && canView('goals') && <button onClick={() => onNavigate('goals')} className="p-2 text-gray-500 hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-lg transition-colors relative" aria-label="Metas y Alertas" title="Ver Metas y Alertas" data-id="element-159" data-mp="2qjga3croz5tb">
          
            <TargetIcon className="w-5 h-5" data-id="element-160" data-mp="349a1o2ul046q" />
            {goalsAlertCount > 0 && <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold ring-2 ring-white dark:ring-gray-800" data-id="element-161" data-mp="39biumz7gf68u">
                {goalsAlertCount > 9 ? '9+' : goalsAlertCount}
              </span>}
          </button>}

        {/* Ayuda / Manual contextual */}
        <button onClick={() => setShowHelp(true)} className="p-2 text-gray-500 hover:text-[#2E8B95] dark:text-gray-400 dark:hover:text-[#5FC1CE] hover:bg-[#5FC1CE]/10 dark:hover:bg-[#5FC1CE]/10 rounded-lg transition-colors" aria-label="Ayuda" title="Ayuda / Manual de usuario" data-id="element-162" data-mp="1bubrta7uge7g">

          <HelpCircleIcon className="w-5 h-5" data-id="element-163" data-mp="1gtey0xzk8qh0" />
        </button>

        {/* Theme toggle */}
        <button onClick={toggleTheme} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'} data-id="element-164" data-mp="1usxxl2eb2thp">
          
          {theme === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-165" data-mp="2djxedgbncwzw" /> : <SunIcon className="w-5 h-5" data-id="element-166" data-mp="2dkqo8q5wk2m9" />}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notificationRef} data-id="element-167" data-mp="29jn7m1g162jz">
          <button onClick={() => setShowNotifications(!showNotifications)} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors relative" aria-label="Notificaciones" data-id="element-168" data-mp="2s6w1x07dv79c">
            
            <BellIcon className="w-5 h-5" data-id="element-169" data-mp="2o49fjetheejj" />
            {unreadCount > 0 && <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold" data-id="element-170" data-mp="29geo01r7mg81">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>}
          </button>

          {showNotifications && <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50" data-id="element-171" data-mp="24h1sytq6q2ud">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-172" data-mp="1vg3wqxlzmzmc">
                <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-173" data-mp="2aywq31wn2yr3">
                  Notificaciones {unreadCount > 0 && `(${unreadCount})`}
                </h3>
                {notifications.length > 0 && <button onClick={markAllAsRead} className="text-sm text-teal-600 dark:text-teal-400 hover:underline" data-id="element-174" data-mp="21wnjuo8elrji">
                
                    Marcar todas como leídas
                  </button>}
              </div>
              <div className="max-h-96 overflow-y-auto" data-id="element-175" data-mp="1n92eeno23g8f">
                {notifications.length === 0 ? <div className="p-8 text-center text-gray-500 dark:text-gray-400" data-id="element-176" data-mp="1i97q8rvo14r2">
                    <BellIcon className="w-12 h-12 mx-auto mb-2 opacity-50" data-id="element-177" data-mp="1e8481yq5awmo" />
                    <p data-id="element-178" data-mp="1e61dr2hymhc5">No hay notificaciones</p>
                  </div> : notifications.map(notification => <div key={notification.id} className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${!notification.read ? 'bg-teal-50 dark:bg-teal-900/10' : ''}`} data-id="element-179" data-mp="149ydk26o4sru">
                
                      <div className="flex items-start gap-3" data-id="element-180" data-mp="3ky7oxbssjat6">
                        <span className="text-2xl flex-shrink-0" data-id="element-181" data-mp="3pwsl904x9acc">
                          {notification.type === 'appointment_created' ? '📅' : notification.type === 'appointment_cancelled' ? '❌' : notification.type === 'appointment_rescheduled' ? '🔄' : notification.type === 'appointment_reminder' ? '⏰' : notification.type === 'low_stock' ? '📦' : '🔔'}
                        </span>
                        <div className="flex-1 min-w-0" data-id="element-182" data-mp="2efj6gqwqn6hd">
                          <div className="flex items-start justify-between gap-2" data-id="element-183" data-mp="2ihw7x34gci8c">
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-184" data-mp="2ifjlacnr1joy">
                              {notification.title}
                            </h4>
                            <button onClick={() => clearNotification(notification.id)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0" data-id="element-185" data-mp="25tzt9ltia8to">
                        
                              <XIcon className="w-4 h-4" data-id="element-186" data-mp="1i6ryhxtpei5w" />
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-187" data-mp="1m8v96k8jtiq4">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-3 mt-2" data-id="element-188" data-mp="1vxdbj5wh7xvv">
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-189" data-mp="1vzhnmb4j4378">
                              {(() => {
                          const diff = new Date().getTime() - new Date(notification.createdAt).getTime();
                          const minutes = Math.floor(diff / 60000);
                          const hours = Math.floor(minutes / 60);
                          const days = Math.floor(hours / 24);
                          if (minutes < 1) return 'Ahora';
                          if (minutes < 60) return `Hace ${minutes} min`;
                          if (hours < 24) return `Hace ${hours}h`;
                          return `Hace ${days}d`;
                        })()}
                            </span>
                            {/* Show who created this notification (admin only) */}
                            {user?.role === 'administrador' && (notification as any).createdByUserName && (notification as any).createdByUserName !== user?.name && <span className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-md font-medium" data-id="element-190" data-mp="3lesbaa786nxx">
                                  {(notification as any).createdByUserName}
                                </span>}
                            {!notification.read && <button onClick={() => markAsRead(notification.id)} className="text-xs text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1" data-id="element-191" data-mp="31ua8po6yvwvh">
                        
                                <CheckIcon className="w-3 h-3" data-id="element-192" data-mp="3bd2nkm3hhlm" /> Marcar como
                                leída
                              </button>}
                          </div>
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>}
        </div>

        {/* User info */}
        <div className="relative pl-4 border-l border-gray-200 dark:border-gray-700" ref={userMenuRef} data-id="element-193" data-mp="m1ufefqj82lc">
          <button onClick={() => setShowUserMenu(!showUserMenu)} className="hidden sm:flex items-center gap-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors p-1 pr-2" aria-label="Menú de perfil" data-id="element-194" data-mp="26s9jsj7t8in0">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-teal-100 dark:bg-teal-900 flex items-center justify-center ring-2 ring-white dark:ring-gray-800 shadow-sm" data-id="element-195" data-mp="2fk1vsychfztp">
              {user?.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" data-id="element-196" data-mp="1nv8gd2twmql6" /> : <UserIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-197" data-mp="1wubya3hc4zy3" />}
            </div>
            <div className="hidden lg:block text-left" data-id="element-198" data-mp="1ru7hsdgfg57v">
              <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-199" data-mp="3898txw80ynef">
                {user?.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-200" data-mp="33axg7aelzsqx">
                {user?.email}
              </p>
            </div>
          </button>

          {showUserMenu && <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50" data-id="element-201" data-mp="7drmlfy7s3kt">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700" data-id="element-202" data-mp="cfgwgs9pab7b">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-203" data-mp="hdigbfeyaeju">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-204" data-mp="qclzski14vyv">
                  {user?.email}
                </p>
              </div>
              <button onClick={() => {
              setShowUserMenu(false);
              onNavigate?.('settings');
            }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left" data-id="element-205" data-mp="v4dr4x90f5q2">
                <UserIcon className="w-4 h-4" data-id="element-206" data-mp="2tlfm25lwa5cq" />
                Ver perfil
              </button>
              <button onClick={() => {
              setShowUserMenu(false);
              logout();
            }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left border-t border-gray-200 dark:border-gray-700" data-id="element-207" data-mp="2pjtzttnh0epv">
                <LogOutIcon className="w-4 h-4" data-id="element-208" data-mp="3d8vevzqt6o44" />
                Cerrar sesión
              </button>
            </div>}
        </div>
      </div>
    </header>

    <HelpDrawer isOpen={showHelp} onClose={() => setShowHelp(false)} currentModule={currentModule} data-id="element-209" data-mp="33itpn4mpyznm" />

    </>;
}